import {t, Trans} from '@lingui/macro'
import styled from 'styled-components'

import {Box, Button, Text, tokens, RadioGroup, RadioButton} from '@pleo-io/telescope'

import {BillingInterval, Interval} from '~/api/types/billing'
import {getFeatureCountryConfiguration} from '~/country-configuration/configuration'
import {Feature} from '~/country-configuration/types'
import type Country from '~/locale/country'

const CommitmentButton = styled(Button)`
    border-width: 1px;
    border-style: solid;
    border-radius: 0;
    border-color: ${({variant}) => (variant === 'primary' ? tokens.shade900 : tokens.shade300)};
    margin-bottom: ${tokens.spacing32};
    padding: ${tokens.spacing8} ${tokens.spacing20};
    position: relative;

    &:first-child {
        border-top-left-radius: ${tokens.arc4};
        border-bottom-left-radius: ${tokens.arc4};
        border-right-width: 0;
    }

    &:last-child {
        border-top-right-radius: ${tokens.arc4};
        border-bottom-right-radius: ${tokens.arc4};
        border-left-width: 0;
    }
`

const RadioButtonBox = styled(Box)`
    margin-bottom: ${tokens.spacing28};
    display: flex;
    justify-content: center;

    &:first-child {
        label {
            padding-right: ${tokens.spacing32};
        }
    }

    &:last-child {
        label {
            padding-right: ${tokens.spacing8};
        }
    }
`

const ButtonPill = styled.div`
    background-color: ${tokens.pink300};
    border-radius: ${tokens.arc4};
    text-transform: uppercase;
    padding: ${tokens.spacing2} ${tokens.spacing6};
    position: absolute;
    top: -${tokens.spacing12};
    right: -${tokens.spacing32};
`

const BillingPill = styled.div`
    background-color: ${tokens.pink300};
    border-radius: ${tokens.arc4};
    text-transform: uppercase;
    padding: ${tokens.spacing2} ${tokens.spacing6};
`

const IntervalSelector = ({
    selectedCommitment,
    onCommitmentChange,
    selectedBilling,
    onBillingChange,
    country,
}: {
    selectedCommitment: Interval
    onCommitmentChange: (interval: Interval.MONTH | Interval.YEAR) => void
    selectedBilling: BillingInterval
    onBillingChange: (billingInterval: BillingInterval) => void
    country: Country
}) => {
    const {isCashBackCountry} = getFeatureCountryConfiguration(Feature.Paywall, country)

    const shouldShowBillingIntervalSelector =
        isCashBackCountry && selectedCommitment === Interval.YEAR

    return (
        <Box>
            <CommitmentButton
                variant={selectedCommitment === Interval.MONTH ? 'primary' : 'secondary'}
                onClick={() => {
                    onCommitmentChange(Interval.MONTH)
                    onBillingChange(BillingInterval.MONTHLY)
                }}
            >
                <Trans>1 month</Trans>
            </CommitmentButton>
            <CommitmentButton
                variant={selectedCommitment === Interval.YEAR ? 'primary' : 'secondary'}
                onClick={() => onCommitmentChange(Interval.YEAR)}
            >
                <Trans>12 months</Trans>
                <ButtonPill>
                    <Text variant="xsmall-subtle" color="shade800">
                        {isCashBackCountry ? t`Cashback` : t`Best price`}
                    </Text>
                </ButtonPill>
            </CommitmentButton>

            {shouldShowBillingIntervalSelector && (
                <BillingSelector selected={selectedBilling} onChange={onBillingChange} />
            )}
        </Box>
    )
}

const BillingSelector = ({
    selected,
    onChange,
}: {
    selected: BillingInterval
    onChange: (billingInterval: BillingInterval) => void
}) => {
    return (
        <RadioGroup
            value={selected}
            onValueChange={onChange}
            defaultValue={BillingInterval.MONTHLY}
            label={t`Billed monthly`}
        >
            <RadioButtonBox data-testid="billing-selector-box">
                <RadioButton value={BillingInterval.MONTHLY} label={t`Billed monthly`} />
                <RadioButton value={BillingInterval.YEARLY} label={t`Billed annually`} />
                <BillingPill>
                    <Text variant="xsmall-subtle" color="shade800">
                        <Trans>Best price</Trans>
                    </Text>
                </BillingPill>
            </RadioButtonBox>
        </RadioGroup>
    )
}

export default IntervalSelector
